"use client";

import staticData from "@/public/data/flight/results.json";
import Flex from "@/shared/components/common/Flex";
import Grid from "@/shared/components/common/Grid";
import List from "@/shared/components/common/List";
import Separator from "@/shared/components/common/separator";
import { useDeviceType } from "@/shared/context/DeviceContext";
import useSessionStorage from "@/shared/hooks/useSessionStorage";
import { isUndefined } from "@/shared/libs/type-utils";
import {
  createSearchParams,
  createUrlWithSearchParams,
} from "@/shared/libs/url-utils";
import type { Flight } from "@/types/services/flight/apis/Availability";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { memo } from "react";
import FlightCardActionButton from "./FlightCardActionButton";
import FlightCardAirlineInfo from "./FlightCardAirlineInfo";
import FlightCardFlightTiming from "./FlightCardFlightTiming";
import FlightCardPanel from "./FlightCardPanel";
import FlightCardPriceInfo from "./FlightCardPriceInfo";
import FlightCardTags from "./FlightcardTags";
import FlightCardToggle from "./FlightCardToggle";
import useFlightCardTabs from "./useFlightCardTabs";
import Typography from "@/shared/components/common/Typography";

const tabs = [
  {
    label: staticData.resultCard.flightDetailLabel,
    panel: "details",
    icon: "AirplaneTilt",
    tab: dynamic(() => import("./FlightCardDetailsTab"), {
      ssr: false,
    }),
  },
  {
    label: staticData.resultCard.flightPriceDetailsLabel,
    panel: "priceDetails",
    icon: "Tag",
    tab: dynamic(() => import("./FlightCardPriceDetailsTab"), {
      ssr: false,
    }),
  },
];

interface FlightCardProps {
  flight: Flight;
  searchParams: SearchParams;
  /** Lets this card retain its normal navigation when used outside results. */
  basePath?: string;
}

export default memo(function FlightCard({
  flight,
  searchParams,
  basePath,
}: FlightCardProps) {
  const { closedFlightLabel } = staticData.resultCard;
  const deviceType = useDeviceType();
  const { activeTab, handleTabChange } = useFlightCardTabs();
  const [_, setDeparture] = useSessionStorage<Flight | null>("departure");
  const pathname = usePathname().toString();
  const resultsPath = basePath ?? pathname;

  const { departFlight, flightType } = searchParams;

  const {
    tags,
    price,
    airline_logo,
    airline_name_lang,
    cabin_type_lang,
    flight_no,
    capacity,
    flight_key,
  } = flight;

  const Tab = tabs.find(({ panel }) => panel === activeTab)?.tab;

  const isBooked = isUndefined(price);

  const NEXT_PAGE_URL_ROUNDTRIP = createUrlWithSearchParams(
    `${resultsPath}${!isUndefined(departFlight) ? "passengers" : ""}`,
    createSearchParams(new URLSearchParams(searchParams), [
      ...(!isUndefined(departFlight)
        ? [
            {
              name: "departFlight",
              value: departFlight,
            },
            {
              name: "returnFlight",
              value: flight_key,
            },
          ]
        : [
            {
              name: "departFlight",
              value: flight_key,
            },
          ]),
    ])
  );

  const NEXT_PAGE_ONEWAY = createUrlWithSearchParams(
    `${resultsPath}passengers`,
    createSearchParams(new URLSearchParams(searchParams), [
      {
        name: "departFlight",
        value: flight_key,
      },
    ])
  );

  const NEXT_PAGE_URL =
    flightType === "one-way" ? NEXT_PAGE_ONEWAY : NEXT_PAGE_URL_ROUNDTRIP;

  const handleSetDepartFlight = () =>
    isUndefined(departFlight) && setDeparture(flight);

  const FlightCard = (
    <Flex
      direction={"col"}
      justifyContent={"between"}
      className="ring-1 ring-border rounded-lg bg-background shadow-1 md:shadow-0 hover:max-md:shadow-2 transition duration-75 ease-linear select-none relative"
    >
      {!capacity && (
        <Typography variant="h4" className="absolute top-6 left-6 md:hidden text-red-500">
          {closedFlightLabel}
        </Typography>
      )}
      <Flex.Item className="w-full p-2">
        <Grid className="grid-cols-2 grid-rows-2 md:grid-rows-1 md:grid-cols-10">
          <Grid.GridCol className="col-span-1 md:col-span-2 row-start-1 row-end-2 md:row-span-1 place-items-start place-content-center">
            <FlightCardAirlineInfo
              name={airline_name_lang}
              logo={airline_logo}
              classType={cabin_type_lang}
              flightNumber={flight_no}
            />
          </Grid.GridCol>

          <Grid.GridCol className="row-start-2 row-end-3 md:row-span-1 col-span-full md:col-span-6 place-items-center place-content-center">
            <FlightCardFlightTiming flight={flight} />
          </Grid.GridCol>

          <Grid.GridCol className="md:hidden col-span-1 md:col-span-2 row-start-1 row-end-2 md:row-span-1 place-items-end place-content-center">
            {tags && <FlightCardTags tags={tags} />}
          </Grid.GridCol>
          <Grid.GridCol className="hidden md:block col-span-1 md:col-span-2 row-start-1 row-end-2 md:row-span-1 place-items-end place-content-center">
            {price && <FlightCardPriceInfo price={price} />}
            {!capacity && (
              <Typography variant="h4" className="mx-auto text-red-500">
                {closedFlightLabel}
              </Typography>
            )}
          </Grid.GridCol>
        </Grid>
      </Flex.Item>

      {capacity && (
        <>
          <Separator direction="horizontal" className="bg-grayColor" />

          <Flex.Item className="w-full p-2">
            <Flex alignItems={"center"} justifyContent={"between"}>
              <Flex.Item className="hidden md:flex">
                <Flex alignItems={"center"} justifyContent={"start"}>
                  <List
                    items={tabs}
                    renderItems={({ label, icon, panel }) => (
                      <FlightCardToggle
                        label={label}
                        icon={icon}
                        isOpen={activeTab === panel}
                        handleToggle={handleTabChange}
                        panel={panel}
                      />
                    )}
                  />
                </Flex>
              </Flex.Item>

              <Flex.Item>
                <Flex alignItems={"center"} gap={"6"}>
                  <Flex.Item className="hidden md:block">
                    {tags && <FlightCardTags tags={tags} />}
                  </Flex.Item>

                  <FlightCardActionButton
                    isBooked={isBooked}
                    nextPageUrl={NEXT_PAGE_URL}
                    remainingSeats={capacity ? +capacity : 0}
                    setFlightKey={handleSetDepartFlight}
                  />
                </Flex>
              </Flex.Item>

              <Flex.Item className="md:hidden">
                <FlightCardPriceInfo price={price} />
              </Flex.Item>
            </Flex>
          </Flex.Item>

          <FlightCardPanel isOpen={!!activeTab}>
            {Tab && <Tab flight={flight} />}
          </FlightCardPanel>
        </>
      )}
    </Flex>
  );

  return deviceType === "mobile" ? (
    <Link href={NEXT_PAGE_URL} onClick={handleSetDepartFlight}>
      {FlightCard}
    </Link>
  ) : (
    FlightCard
  );
});
